// ============================================
// DashboardPage Component
// Main dashboard page with overview stats and data
// ============================================

import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ADMIN_ROUTES } from '@conai/shared-constants';
import type { InventoryListParams } from '@conai/shared-types';
import type { TopProductItem } from '../../hooks';
import { ALL_TIME_START, aggregateStatusCounts, todayIso } from '../../utils/orderStatus';
import { PageHeader, PageContainer } from '../../components/shared';
import { usePaymentStats } from '../../hooks/usePayments';
import { useStoreSelectionStore } from '../../stores';
import { useActiveCurrency } from '../../hooks/useActiveCurrency';
import { useCurrencyScope, useMoney } from '../../providers/CurrencyProvider';
import {
  useCustomerSummary,
  useDashboardOverview,
  useInventory,
  useLowStock,
  useOrderStats,
  useProducts,
  useRecentDashboardTransactions,
  useRevenueTrend,
  useSuppliers,
  useTopProducts,
  useTopProductsByCategory,
} from '../../hooks';
import { resolveDateLocale, useI18n } from '../../i18n';
import { getImageUrl } from '../../utils/image';
import { resolveQuickPeriodRange, type QuickPeriod } from '../../utils/periodRange';
import {
  AlertBanner,
  DashboardStatsRow,
  RevenueChart,
  AdvancedInsightsPanel,
  OrderStatusGrid,
  BestSellingTable,
  LowStockWarningTable,
  RecentTransactionsTable,
  TopCategoriesPanel,
  type DashboardStats,
  type RevenueDataPoint,
  type InsightsData,
  type OrderStatusData,
  type BestSellingProduct,
  type LowStockItem,
  type Transaction,
} from './components';

type ListEnvelope<T> = {
  results?: T[];
  items?: T[];
  data?: T[] | { results?: T[]; items?: T[]; data?: T[] };
  count?: number;
  total?: number;
  meta?: { count?: number };
};

function extractList<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[];

  const data = payload as ListEnvelope<T> | undefined;
  if (!data) return [];
  if (Array.isArray(data.data)) return data.data;
  if (data.data && typeof data.data === 'object') {
    return data.data.data ?? data.data.results ?? data.data.items ?? [];
  }
  return data.results ?? data.items ?? [];
}

function extractCount<T>(payload: unknown, fallback: number): number {
  if (Array.isArray(payload)) return payload.length;

  const data = payload as ListEnvelope<T> | undefined;
  return data?.meta?.count ?? data?.count ?? data?.total ?? fallback;
}

function toNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}

function toText(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function toDashboardPaymentStatus(value: unknown): Transaction['status'] {
  const status = toText(value).trim().toUpperCase();

  if (status === 'COMPLETED' || status === 'PAID') return 'completed';
  if (status === 'REFUNDED') return 'refunded';
  if (status === 'FAILED' || status === 'CANCELLED' || status === 'DECLINED') return 'cancelled';
  return 'pending';
}

const DAY_MS = 24 * 60 * 60 * 1000;

function toIsoDate(value: Date): string {
  return value.toISOString().split('T')[0];
}

function normalizeOrderStatus(value: unknown): string {
  return toText(value).trim().toUpperCase();
}

// Tồn kho hiện tại và ngưỡng cảnh báo — mỗi endpoint đặt tên field một kiểu:
//   /inventory/low-stock/          -> current_stock / min_stock_threshold
//   /inventory/sellable-products/  -> quantity / min_quantity
//   /reports/low-stock/            -> current_stock / min_threshold
// Đọc thiếu alias nào là cột đó về 0 — chính là lý do cột MINIMUM của bảng
// "Low Product Warning" luôn hiện "0 units", kéo theo mọi dòng đều bị xếp trạng
// thái "In stock" (ngưỡng 0 thì không bao giờ có gì thủng ngưỡng).
function readStock(item: Record<string, unknown>): number {
  return toNumber(item.current_stock ?? item.quantity ?? item.available_stock ?? item.available_quantity);
}

function readMinimum(item: Record<string, unknown>): number {
  return toNumber(item.min_stock_threshold ?? item.min_threshold ?? item.min_quantity);
}

/**
 * Mức tồn tối đa của kho cho mặt hàng này — mốc 100% khi tính "còn bao nhiêu %".
 * Trả null khi BE không có số: KHÔNG suy diễn (bản cũ của trang Inventory từng
 * bịa `max = max(min*5, quantity, 100)`, ra một tỷ lệ vô nghĩa).
 */
function readMaximum(item: Record<string, unknown>): number | null {
  const raw = item.max_stock_threshold ?? item.max_quantity ?? item.max_stock;
  const value = toNumber(raw, NaN);
  return Number.isFinite(value) && value > 0 ? value : null;
}

/** Phần trăm tồn còn lại so với kho đầy; null khi không biết mức tối đa. */
function readPercentLeft(item: Record<string, unknown>): number | null {
  const max = readMaximum(item);
  if (max === null) return null;
  return (readStock(item) / max) * 100;
}

/** Còn từ mức này trở xuống thì coi là sắp hết và đưa vào bảng cảnh báo. */
const LOW_STOCK_PERCENT_THRESHOLD = 30;

const CATEGORY_COLORS = ['#F59E0B', '#5BB8F5', '#1D3461', '#10B981', '#EF4444'];

// LƯU Ý: tiền vs đơn dùng 2 định nghĩa tách bạch.
// - TIỀN (Revenue chart, Total Transactions): mặc định BE = COMPLETED + PAID,
//   tức chỉ tiền đã thu thật — không truyền order_status.
// - ĐƠN (Total Orders, Operations Overview): mọi đơn trừ huỷ/hoàn, đếm qua
//   /reports/order-status-trend/ với ORDER_STATUS_GROUPS ở trên.

// Vòng đời đơn của BE là luồng pha chế/nhận tại quầy:
// DRAFT -> CONFIRMED -> PREPARING -> READY -> COMPLETED (không có khâu giao vận).
// Nhóm hiển thị trên Operations Overview:
// Định nghĩa nhóm/đếm đơn nằm ở utils/orderStatus.ts — DÙNG CHUNG với trang
// Orders. Đừng khai báo lại tại đây: hai bản copy là mầm mống lệch số.

// ============================================
// Component
// ============================================

export function DashboardPage() {
  const navigate = useNavigate();
  const { selectedStoreId, availableStores, setSelectedStoreById } = useStoreSelectionStore();

  // Tiền tệ theo cửa hàng đang chọn (sheet 2 #75).
  // LƯU Ý: ở chế độ toàn chuỗi nhiều thị trường, các thẻ tổng của dashboard vẫn
  // cộng chéo tiền tệ — cùng hạn chế với Reports, đã cảnh báo ở trang Reports.
  useCurrencyScope(useActiveCurrency());
  const money = useMoney();
  const { t, language } = useI18n();

  // State - persisted in sessionStorage to survive navigation
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);

  // Default = 30 ngày gần nhất. Range ngắn giúp BE gom bucket theo ngày và
  // phép so sánh cùng kỳ năm trước mới có ý nghĩa. Người dùng vẫn tự mở rộng được.
  const getDefaultStartDate = useCallback(() => {
    const from = new Date();
    from.setDate(from.getDate() - 29);
    return toIsoDate(from);
  }, []);

  const getDefaultEndDate = useCallback(() => toIsoDate(new Date()), []);

  const [startDate, setStartDateState] = useState(() => {
    try {
      const stored = sessionStorage.getItem('dashboard_start_date_v2');
      return stored || getDefaultStartDate();
    } catch { return getDefaultStartDate(); }
  });

  const [endDate, setEndDateState] = useState(() => {
    try {
      const stored = sessionStorage.getItem('dashboard_end_date_v2');
      return stored || getDefaultEndDate();
    } catch { return getDefaultEndDate(); }
  });

  const setStartDate = useCallback((val: string) => {
    setStartDateState(val);
    try { sessionStorage.setItem('dashboard_start_date_v2', val); } catch {}
  }, []);

  const setEndDate = useCallback((val: string) => {
    setEndDateState(val);
    try { sessionStorage.setItem('dashboard_end_date_v2', val); } catch {}
  }, []);

  // Close date picker on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target as Node)) {
        setDatePickerOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAlert, setShowAlert] = useState(true);
  const pageSize = 10;

  // Bug #32: hai dropdown "Today / This week / This month" của panel
  // "Overall Information" (khối Customers Overview) và "Top Categories" trước
  // đây giữ state BÊN TRONG component và không truyền đi đâu — chọn giá trị nào
  // biểu đồ cũng vẽ y hệt vì query vẫn chạy với khoảng ngày cũ. State lên đây,
  // đúng nơi gọi API, rồi quy ra start_date/end_date qua utils/periodRange.
  //
  // Hai panel giữ hai state riêng: người dùng chỉnh dropdown này thì panel kia
  // không được đổi theo (chúng là hai bộ lọc độc lập trên UI).
  const [customersPeriod, setCustomersPeriod] = useState<QuickPeriod>('today');
  const [categoriesPeriod, setCategoriesPeriod] = useState<QuickPeriod>('today');

  const customersRange = useMemo(() => resolveQuickPeriodRange(customersPeriod), [customersPeriod]);
  const categoriesRange = useMemo(() => resolveQuickPeriodRange(categoriesPeriod), [categoriesPeriod]);

  const formatDisplayDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString(resolveDateLocale(language, 'en-GB'), { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const displayRange = `${formatDisplayDate(startDate)} - ${formatDisplayDate(endDate)}`;

  const {
    data: dashboardOverviewData,
    isLoading: isLoadingDashboardOverview,
  } = useDashboardOverview({ store_id: selectedStoreId ?? undefined });

  const {
    data: revenueTrendData,
    isLoading: isLoadingRevenue,
  } = useRevenueTrend({
    start_date: startDate,
    end_date: endDate,
    store_id: selectedStoreId ?? undefined,
  });

  // Cùng kỳ năm trước: lùi đúng range đang chọn 1 năm để ghép thành đường so sánh.
  const previousYearRange = useMemo(() => {
    const shift = (iso: string) => {
      const date = new Date(iso);
      if (Number.isNaN(date.getTime())) return iso;
      date.setFullYear(date.getFullYear() - 1);
      return toIsoDate(date);
    };
    return { start_date: shift(startDate), end_date: shift(endDate) };
  }, [startDate, endDate]);

  const { data: previousRevenueTrendData } = useRevenueTrend({
    start_date: previousYearRange.start_date,
    end_date: previousYearRange.end_date,
    store_id: selectedStoreId ?? undefined,
  });

  // Kỳ liền trước, cùng độ dài — baseline thật cho % thay đổi của các thẻ
  // tính theo khoảng ngày (Total Orders, Total Transactions).
  const previousPeriodRange = useMemo(() => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return { start_date: startDate, end_date: endDate };
    }
    const lengthMs = end.getTime() - start.getTime() + DAY_MS;
    return {
      start_date: toIsoDate(new Date(start.getTime() - lengthMs)),
      end_date: toIsoDate(new Date(end.getTime() - lengthMs)),
    };
  }, [startDate, endDate]);

  const { data: previousPeriodData } = useRevenueTrend({
    start_date: previousPeriodRange.start_date,
    end_date: previousPeriodRange.end_date,
    store_id: selectedStoreId ?? undefined,
  });

  // Hôm qua — baseline thật cho badge "vs yesterday" của 2 thẻ theo ngày.
  const yesterdayIso = useMemo(() => toIsoDate(new Date(Date.now() - DAY_MS)), []);

  // Số giao dịch PAID hôm nay/hôm qua từ /payments/stats/ (BE đếm, đúng scope
  // store, lọc theo requested_at) — thay cho trò đếm 50 dòng gần nhất phía client.
  const { data: todayPaymentStats } = usePaymentStats({
    store_id: selectedStoreId ?? undefined,
    start_date: todayIso(),
    end_date: todayIso(),
  });

  const { data: yesterdayPaymentStats } = usePaymentStats({
    store_id: selectedStoreId ?? undefined,
    start_date: yesterdayIso,
    end_date: yesterdayIso,
  });

  const { data: yesterdayData } = useRevenueTrend({
    start_date: yesterdayIso,
    end_date: yesterdayIso,
    store_id: selectedStoreId ?? undefined,
  });

  // /orders/stats/ — nguồn đếm đơn CHUẨN: dùng chung get_queryset với /orders/
  // nên cùng scope/soft-delete với bảng ở trang Orders.
  // ALL-TIME nhưng truyền ngày TƯỜNG MINH: bỏ trống ngày thì BE mặc định một
  // cửa sổ hẹp (chỉ ra vài đơn hôm nay) chứ không phải all-time.
  const {
    data: orderStatsData,
    isLoading: isLoadingOrderStatus,
  } = useOrderStats({
    store_id: selectedStoreId ?? undefined,
    start_date: ALL_TIME_START,
    end_date: todayIso(),
  });

  // Bug #32: bám dropdown của chính panel "Top Categories", KHÔNG bám date range
  // ở header. `period: 'daily'` là độ mịn bucket của BE (daily/weekly/monthly),
  // không phải bộ lọc — dữ liệu vẫn được gom hết lại nên để 'daily' là đủ.
  //
  // `keepPreviousData`: đổi kỳ là đổi queryKey, không bật lại `isLoading` -> panel
  // giữ số của kỳ cũ thay vì nháy thành khối skeleton xám.
  const {
    data: topProductsByCategoryData,
    isLoading: isLoadingTopCategories,
    isFetching: isFetchingTopCategories,
    isPlaceholderData: isPlaceholderTopCategories,
  } = useTopProductsByCategory(
    {
      start_date: categoriesRange.start_date,
      end_date: categoriesRange.end_date,
      period: 'daily',
      store_id: selectedStoreId ?? undefined,
      limit_per_category: 5,
    },
    { keepPreviousData: true }
  );

  const {
    data: topProductsData,
    isLoading: isLoadingTopProducts,
  } = useTopProducts({
    start_date: startDate,
    end_date: endDate,
    store_id: selectedStoreId ?? undefined,
    limit: 20,
  });

  const {
    data: suppliersData,
    isLoading: isLoadingSuppliers,
  } = useSuppliers({
    page: 1,
    page_size: 1,
    ...(selectedStoreId ? { store_id: selectedStoreId } : {}),
  });

  // Thẻ "Customer" ở khối trên của panel bám date range của header như các thẻ
  // Suppliers/Orders cạnh nó — khối đó không có dropdown riêng.
  const {
    data: customerSummaryData,
    isLoading: isLoadingCustomerSummary,
  } = useCustomerSummary({
    start_date: startDate,
    end_date: endDate,
    store_id: selectedStoreId ?? undefined,
  });

  // Bug #32: donut "Customers Overview" bám dropdown ngay trên nó. Query riêng
  // (không dùng chung với thẻ "Customer" phía trên) vì hai khối có hai bộ lọc
  // khác nhau — gộp làm một là thẻ trên lại đổi theo dropdown của khối dưới.
  //
  // `keepPreviousData`: xem ghi chú ở useTopProductsByCategory phía trên.
  const {
    data: customersOverviewData,
    isLoading: isLoadingCustomersOverview,
    isFetching: isFetchingCustomersOverview,
    isPlaceholderData: isPlaceholderCustomersOverview,
  } = useCustomerSummary(
    {
      start_date: customersRange.start_date,
      end_date: customersRange.end_date,
      store_id: selectedStoreId ?? undefined,
    },
    { keepPreviousData: true }
  );

  // Đang tải NỀN dữ liệu kỳ mới (số cũ vẫn hiển thị) -> chỉ làm mờ nhẹ phần thân.
  // Dùng `isFetching && isPlaceholderData` chứ KHÔNG dùng `isFetching` trần: mọi
  // refetch nền bình thường (focus lại tab, invalidate...) cũng bật `isFetching`
  // và sẽ làm panel nhấp nháy dù người dùng không đổi kỳ.
  const isRefreshingCustomersOverview =
    isFetchingCustomersOverview && isPlaceholderCustomersOverview;
  const isRefreshingTopCategories =
    isFetchingTopCategories && isPlaceholderTopCategories;

  // Bảng giao dịch bám ĐÚNG khoảng ngày đang chọn trên header, như mọi widget
  // khác của trang. Trước đây nó không có cận ngày nào nên ngày 07/08 vẫn hiện
  // giao dịch test từ 22/07 — vừa gây nhiễu vừa bắt BE quét cả bảng.
  // Datetime gửi đi KHÔNG kèm offset để server hiểu theo múi giờ của nó (xem
  // cùng lý do ở bộ lọc ngày trang Orders).
  const {
    data: recentTransactionsData,
    isLoading: isLoadingTransactions,
  } = useRecentDashboardTransactions({
    page: 1,
    page_size: 50,
    ordering: '-requested_at',
    store_id: selectedStoreId ?? undefined,
    requested_at__gte: `${startDate}T00:00:00`,
    requested_at__lte: `${endDate}T23:59:59`,
  });


  // API Queries
  const {
    data: lowStockData,
    isLoading: isLoadingLowStock,
  } = useLowStock(
    selectedStoreId ?? undefined,
    { page_size: 50 },
    // Bug #23: "Low Product Warning" phải phản ánh tồn kho gần realtime — bán hàng
    // diễn ra ở app POS nên dashboard phải tự poll + refetch khi quay lại tab,
    // không thể dựa vào cache 60s mặc định.
    //
    // Bug #24: chỉ poll thôi CHƯA đủ. queryClient đặt refetchOnMount:false toàn
    // cục, nên đi Inventory rồi bấm Dashboard là thấy lại số cũ và phải chờ hết
    // 30s mới cập nhật. 'always' bắt lấy số mới ngay khi trang mở.
    {
      enabled: true,
      refetchInterval: 30_000,
      refetchOnWindowFocus: true,
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );

  // Toàn bộ tồn kho, dùng cho bảng "Low Product Warning": kể cả khi chưa có item
  // nào thủng ngưỡng, bảng vẫn hiển thị các mặt hàng tồn thấp nhất (data thật).
  const {
    data: inventoryData,
    isLoading: isLoadingInventory,
  } = useInventory(
    {
      ...(selectedStoreId ? ({ store_id: selectedStoreId } as unknown as InventoryListParams) : {}),
      page_size: 100,
    },
    // Bug #23/#24: cùng lý do với useLowStock phía trên.
    { refetchInterval: 30_000, refetchOnWindowFocus: true, staleTime: 0, refetchOnMount: 'always' }
  );

  // Bug #22: nguồn ảnh dự phòng cho Best Selling Products — inventory/low-stock
  // chỉ phủ store đang chọn + tối đa 100 record nên nhiều sản phẩm không có ảnh.
  // Danh sách products là nguồn ảnh đầy đủ theo product id.
  const { data: productsForImagesData } = useProducts({ page: 1, page_size: 200 });

  const lowStockRawItems = useMemo(() => extractList<Record<string, unknown>>(lowStockData), [lowStockData]);

  // Một nguồn duy nhất: count của /inventory/low-stock/. Bỏ chuỗi fallback qua
  // alerts.low_stock_count của /reports/dashboard/ vì hai endpoint đếm theo phạm
  // vi khác nhau (snapshot toàn hệ thống vs đã lọc store), dễ ra hai số vênh nhau.
  const lowStockCount = extractCount<Record<string, unknown>>(lowStockData, lowStockRawItems.length);

  const outOfStockItems = useMemo(
    () => lowStockRawItems.filter((item) => readStock(item) <= 0),
    [lowStockRawItems]
  );

  const outOfStockCount = outOfStockItems.length;

  const revenueSeries = revenueTrendData?.data ?? [];
  const todayRevenue = toNumber(dashboardOverviewData?.today?.revenue);

  // Tổng của đúng khoảng ngày + store đang chọn.
  const rangeTotalRevenue = toNumber(revenueTrendData?.totals?.revenue);

  // Không whitelist status — status lạ dồn vào "other", vẫn cộng vào total.
  const aggregatedOrderStatus = useMemo(() => {
    const agg = aggregateStatusCounts(orderStatsData?.status_counts);
    return {
      ...agg,
      // countable_orders do BE tính theo ORDER_STATUSES_COUNTABLE — ưu tiên
      // số của BE, fallback tự gom khi field chưa có.
      total: orderStatsData?.countable_orders ?? agg.total,
    };
  }, [orderStatsData]);

  // Thẻ "Today's Revenue" chỉ được lấy doanh thu hôm nay. Fallback cũ
  // (|| rangeTotalRevenue) khiến hôm nay chưa bán được gì thì thẻ lại hiện tổng
  // cả khoảng đang chọn — cùng loại lỗi "fake giá trị tiền" của bug #16.
  const totalRevenue = todayRevenue;

  // Dùng đúng một nguồn với Operations Overview. Bỏ chuỗi fallback cũ
  // (|| monthOrders || weekOrders || ...) vì khi total = 0 nó nhảy sang nguồn
  // khác khoảng thời gian, làm 2 widget lệch nhau.
  const totalOrders = aggregatedOrderStatus.total;

  // Tổng tiền giao dịch trong đúng khoảng ngày + store đang chọn.
  // Trước đây lấy monthRevenue/weekRevenue từ dashboard-overview — endpoint này
  // không nhận start_date/end_date nên giá trị không đổi khi lọc, gây cảm giác "fake".
  const totalTransactionsAmount = rangeTotalRevenue;

  const suppliersTotal = extractCount<Record<string, unknown>>(suppliersData, 0);

  const transactionsRawItemsRaw = useMemo(
    () => extractList<Record<string, unknown>>(recentTransactionsData),
    [recentTransactionsData]
  );

  /**
   * MỘT ĐƠN = MỘT DÒNG, giữ lần thanh toán mới nhất.
   *
   * `/payments/` trả mỗi LẦN THANH TOÁN một bản ghi. Một đơn bị fail rồi khách
   * thử lại vài lần sẽ đẻ ra 4-5 dòng trùng hệt nhau về sản phẩm và số tiền, chỉ
   * khác trạng thái (Pending / Cancelled / Completed).
   *
   * BE đã gom sẵn khi FE gửi `latest_per_order=true` (xem useRecentDashboardTransactions),
   * nhưng tham số đó chỉ bật được khi có store_id. Lớp gom dưới đây giữ lại làm
   * lưới an toàn cho trường hợp xem tất cả cửa hàng; khi BE đã gom rồi thì nó là
   * no-op vì mỗi đơn vốn chỉ còn một bản ghi.
   */
  const transactionsRawItems = useMemo(() => {
    const timeOf = (item: Record<string, unknown>): number => {
      const raw = toText(item.requested_at) || toText(item.confirmed_at) || toText(item.created_at);
      const parsed = new Date(raw).getTime();
      return Number.isNaN(parsed) ? 0 : parsed;
    };

    // Chỉ gom khi CHẮC CHẮN là cùng một giao dịch ngoài đời:
    //  - cùng `order`  -> các lần thử thanh toán của cùng một đơn
    //  - cùng `provider_transaction_id` -> cùng một giao dịch ở cổng thanh toán
    // KHÔNG gom theo (sản phẩm + số tiền + thời gian): hai khách mua cùng món,
    // cùng giá, cùng phút là chuyện bình thường ở bán lẻ — gom kiểu đó là tự tay
    // xoá doanh thu thật khỏi báo cáo.
    const groupKeyOf = (item: Record<string, unknown>): string =>
      toText(item.order) || toText(item.order_code) || toText(item.provider_transaction_id);

    const latestByGroup = new Map<string, Record<string, unknown>>();
    const standalone: Record<string, unknown>[] = [];

    for (const item of transactionsRawItemsRaw) {
      const key = groupKeyOf(item);
      if (!key) {
        standalone.push(item);
        continue;
      }

      const current = latestByGroup.get(key);
      if (!current || timeOf(item) >= timeOf(current)) {
        latestByGroup.set(key, item);
      }
    }

    return [...latestByGroup.values(), ...standalone].sort((a, b) => timeOf(b) - timeOf(a));
  }, [transactionsRawItemsRaw]);

  /** Gộp tên sản phẩm của một đơn thành một dòng ngắn: "A, B +3". */
  const describeOrderItems = useCallback(
    (rawItems: unknown, itemsCount: number): string => {
      const items = Array.isArray(rawItems) ? (rawItems as Record<string, unknown>[]) : [];
      const names = items.map((entry) => toText(entry.product_name)).filter(Boolean);

      if (names.length === 0) {
        return itemsCount > 0 ? t('itemsCount', { count: itemsCount }) : '—';
      }
      if (names.length > 2) {
        return `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
      }
      return names.join(', ');
    },
    [t]
  );

  const transactions: Transaction[] = useMemo(
    () =>
      transactionsRawItems.map((item, index) => {
        const transactionId =
          toText(item.provider_transaction_id) ||
          toText(item.transaction_id) ||
          toText(item.id) ||
          `transaction-${index + 1}`;

        return {
          id: toText(item.id) || transactionId,
          transactionId,
          // Bug #28: cột này từng hiển thị order_code — đọc lên y hệt một mã giao
          // dịch nữa. /payments/ giờ trả thẳng customer_name (null = khách vãng lai).
          customerName: toText(item.customer_name) || t('guest'),
          // Bug #27: cột này từng hiển thị tên cổng thanh toán (provider).
          productName: describeOrderItems(item.order_items, toNumber(item.items_count)),
          amount: toNumber(item.amount),
          // Cổng thanh toán chỉ hiện trong tooltip — hai giao dịch cùng phút,
          // cùng số tiền vẫn phân biệt được nhờ nó.
          provider: toText(item.provider) || undefined,
          status: toDashboardPaymentStatus(item.payment_status ?? item.status),
          createdAt:
            toText(item.requested_at) ||
            toText(item.confirmed_at) ||
            toText(item.created_at) ||
            new Date().toISOString(),
        };
      }),
    [transactionsRawItems, describeOrderItems, t]
  );

  // Giao dịch = PAID only, do /payments/stats/ đếm (đúng scope + đủ dữ liệu).
  const todayTransactions = todayPaymentStats?.paid_count ?? 0;
  const yesterdayTransactions = yesterdayPaymentStats?.paid_count ?? 0;

  /** Trả null khi không có baseline thật — UI sẽ ẩn badge thay vì hiện 0%. */
  const calcChange = (current: number, baseline: number): number | null => {
    if (baseline <= 0) return null;
    return Number((((current - baseline) / baseline) * 100).toFixed(1));
  };

  // Baseline lấy từ API thật, không suy diễn từ trung bình tuần/tháng nữa.
  const yesterdayRevenue = toNumber(yesterdayData?.totals?.revenue);
  const previousPeriodRevenue = toNumber(previousPeriodData?.totals?.revenue);

  const stats: DashboardStats = {
    totalRevenue,
    revenueChange: calcChange(totalRevenue, yesterdayRevenue),
    totalOrders,
    // Total Orders là con số all-time nên không có "kỳ trước" để so — ẩn badge.
    ordersChange: null,
    totalTransactions: totalTransactionsAmount,
    transactionsChange: calcChange(totalTransactionsAmount, previousPeriodRevenue),
    todayTransactions,
    todayTransactionsChange: calcChange(todayTransactions, yesterdayTransactions),
  };

  // BE trả chuỗi liên tục (kỳ không có đơn -> revenue "0.00"), FE không cần gap-fill.
  // Bucket do BE tự chọn theo độ dài range, nên format nhãn theo `period` trả về.
  const revenuePeriod = revenueTrendData?.period;
  const previousRevenueSeries = previousRevenueTrendData?.data ?? [];

  const revenueData: RevenueDataPoint[] = useMemo(() => {
    const formatLabel = (value: string) => {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return revenuePeriod === 'monthly'
        ? date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
        : date.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
    };

    // Chỉ vẽ đường Last Year khi kỳ trước thực sự có đơn. BE luôn fill đủ bucket
    // nên chuỗi rỗng sẽ là một dãy "0.00" chứ không phải mảng trống.
    const hasPreviousOrders = previousRevenueSeries.some((item) => toNumber(item.order_count) > 0);

    // Hai chuỗi đều dense nên bucket thứ i tương ứng 1-1. Align từ cuối để lệch
    // độ dài do năm nhuận (366 vs 365 ngày) rơi vào đầu chuỗi, không phải cuối.
    const offset = previousRevenueSeries.length - revenueSeries.length;

    return revenueSeries.map((item, index) => {
      const matched = hasPreviousOrders ? previousRevenueSeries[index + offset] : undefined;

      return {
        day: formatLabel(item.date),
        current: toNumber(item.revenue),
        previous: matched ? toNumber(matched.revenue) : null,
        previousLabel: matched ? formatLabel(matched.date) : undefined,
      };
    });
  }, [revenueSeries, previousRevenueSeries, revenuePeriod]);

  const totalCustomers = toNumber(customerSummaryData?.total_customers);

  // Bug #32: 4 số của donut đọc từ query THEO KỲ ĐANG CHỌN. Trước đây chúng dùng
  // chung response với thẻ "Customer" nên dropdown đổi mà donut đứng im.
  const firstTimeCustomers = toNumber(customersOverviewData?.first_time_customers);
  const returningCustomers = toNumber(customersOverviewData?.returning_customers);
  const firstTimeChange = toNumber(customersOverviewData?.first_time_change_pct);
  const returningChange = toNumber(customersOverviewData?.returning_change_pct);

  const insightsData: InsightsData = {
    stockCount: lowStockCount,
    suppliers: suppliersTotal,
    customers: totalCustomers,
    orders: totalOrders,
    firstTimeCustomers,
    returnCustomers: returningCustomers,
    firstTimeChange,
    returnChange: returningChange,
  };

  const orderStatusCardData: OrderStatusData = {
    pending: aggregatedOrderStatus.pending,
    processing: aggregatedOrderStatus.processing,
    ready: aggregatedOrderStatus.ready,
    completed: aggregatedOrderStatus.completed,
    total: aggregatedOrderStatus.total,
  };

  // /reports/top-products/ không trả ảnh — mượn image_url từ dữ liệu tồn kho
  // (đã fetch sẵn cho bảng Low Stock) map theo product_id. (bug #20)
  const productImageById = useMemo(() => {
    const map = new Map<string, string>();
    const collect = (payload: unknown) => {
      for (const item of extractList<Record<string, unknown>>(payload)) {
        // inventory/low-stock: khóa là product_id; products: khóa là id.
        const id = toText(item.product_id) || toText(item.id);
        const imageUrls = Array.isArray(item.image_urls) ? item.image_urls : [];
        const img = toText(item.image_url) || toText(imageUrls[0]);
        if (id && img && !map.has(id)) map.set(id, img);
      }
    };
    collect(inventoryData);
    collect(lowStockData);
    // Bug #22: fallback từ danh sách products (đầy đủ nhất).
    collect(productsForImagesData);
    return map;
  }, [inventoryData, lowStockData, productsForImagesData]);

  // Dùng /reports/top-products/ thay cho khối top_products của /reports/dashboard/:
  // endpoint riêng nhận start_date/end_date nên khớp date picker, và group theo
  // product_id nên 2 sản phẩm trùng tên không bị gộp làm một.
  const bestSelling: BestSellingProduct[] = useMemo(
    () =>
      extractList<TopProductItem>(topProductsData).map((item, index) => {
        const productId = toText(item.product_id);
        // Bug #22: ưu tiên image_url do chính /reports/top-products/ trả (nếu có),
        // sau đó mới tới map ảnh từ inventory/low-stock/products.
        const image =
          toText((item as unknown as Record<string, unknown>).image_url) ||
          (productId ? productImageById.get(productId) : undefined);
        return {
          id: productId || `top-product-${index + 1}`,
          name: toText(item.product_name) || 'Unknown Product',
          imageUrl: image ? getImageUrl(image) : undefined,
          sold: toNumber(item.quantity_sold),
          revenue: toNumber(item.revenue),
          // trend: bỏ trống — /reports/top-products/ chưa trả dữ liệu xu hướng.
        };
      }),
    [topProductsData, productImageById]
  );

  const topCategoriesComputed = useMemo(() => {
    const categoryMap = new Map<string, { sales: number; products: Set<string> }>();
    const allProducts = new Set<string>();

    for (const point of topProductsByCategoryData?.data ?? []) {
      for (const category of point.categories ?? []) {
        const categoryName = toText(category.category_name) || 'Unknown Category';
        const existing = categoryMap.get(categoryName) ?? { sales: 0, products: new Set<string>() };

        for (const product of category.products ?? []) {
          const sold = toNumber(product.quantity_sold);
          const productId = toText(product.product_id) || toText(product.product_name);
          existing.sales += sold;
          if (productId) {
            existing.products.add(productId);
            allProducts.add(productId);
          }
        }

        categoryMap.set(categoryName, existing);
      }
    }

    const sorted = [...categoryMap.entries()]
      .map(([label, value], idx) => ({
        label,
        sales: value.sales,
        color: CATEGORY_COLORS[idx % CATEGORY_COLORS.length],
      }))
      .sort((a, b) => b.sales - a.sales)
      .slice(0, 3);

    const hasPositive = sorted.some((item) => item.sales > 0);

    return {
      categories: hasPositive ? sorted : [],
      totalCategories: categoryMap.size,
      totalProducts: allProducts.size,
    };
  }, [topProductsByCategoryData]);

  const delayedOrders = useMemo(() => {
    const now = Date.now();
    return (dashboardOverviewData?.recent_orders ?? [])
      .filter((order) => {
        const status = normalizeOrderStatus(order.status);
        if (status === 'COMPLETED' || status === 'PAID' || status === 'CANCELLED' || status === 'VOIDED') {
          return false;
        }
        const createdAt = new Date(toText(order.created_at)).getTime();
        if (Number.isNaN(createdAt)) {
          return false;
        }
        return (now - createdAt) / (60 * 1000) >= 15;
      })
      .slice(0, 4);
  }, [dashboardOverviewData]);

  // Ưu tiên item từ /inventory/low-stock (đã thủng ngưỡng), sau đó bù thêm các
  // mặt hàng tồn thấp nhất từ danh sách tồn kho để bảng luôn có dữ liệu thật.
  const inventoryRawItems = useMemo(
    () => extractList<Record<string, unknown>>(inventoryData),
    [inventoryData]
  );

  const stockWatchRawItems = useMemo(() => {
    const merged = [...lowStockRawItems];
    const seen = new Set(merged.map((item) => toText(item.product_id)));

    for (const item of inventoryRawItems) {
      const key = toText(item.product_id);
      if (!seen.has(key)) {
        seen.add(key);
        merged.push(item);
      }
    }

    return merged.sort((a, b) => readStock(a) - readStock(b));
  }, [lowStockRawItems, inventoryRawItems]);

  const toLowStockItem = useCallback((item: Record<string, unknown>, index: number): LowStockItem => {
    const stock = readStock(item);
    const minimum = readMinimum(item);
    const percentLeft = readPercentLeft(item);
    const imageUrl = toText(item.image_url);

    let status: LowStockItem['status'] = 'ok';
    if (stock <= 0) {
      status = 'critical';
    } else if (minimum > 0 && stock <= minimum) {
      status = 'warning';
    } else if (percentLeft !== null && percentLeft <= LOW_STOCK_PERCENT_THRESHOLD) {
      status = 'low';
    }

    return {
      id: toText(item.product_id) || `low-stock-${index + 1}`,
      productName: toText(item.product_name) || 'Unknown Product',
      imageUrl: imageUrl ? getImageUrl(imageUrl) : undefined,
      stock,
      stockUnit: 'units',
      percentLeft,
      status,
    };
  }, []);

  // CHỈ những mặt hàng thực sự còn ít. Sản phẩm còn gần như nguyên kho không có
  // lý do gì xuất hiện dưới tiêu đề "Low Product Warning".
  const lowStockItems: LowStockItem[] = useMemo(
    () =>
      stockWatchRawItems
        .filter((item) => {
          const stock = readStock(item);
          if (stock <= 0) return true;

          const percentLeft = readPercentLeft(item);
          if (percentLeft !== null) {
            return percentLeft <= LOW_STOCK_PERCENT_THRESHOLD;
          }

          // Không có mức tồn tối đa để tính % — lùi về so với ngưỡng tối thiểu.
          const minimum = readMinimum(item);
          return minimum > 0 && stock <= minimum;
        })
        .slice(0, 20)
        .map(toLowStockItem),
    [stockWatchRawItems, toLowStockItem]
  );

  // Handlers
  const handleAlertReview = () => {
    setAlertPopupOpen(true);
  };

  const handleAlertDismiss = () => {
    setShowAlert(false);
  };

  const [alertPopupOpen, setAlertPopupOpen] = useState(false);

  const handleViewAllProducts = () => {
    navigate(ADMIN_ROUTES.PRODUCTS);
  };

  const handleReorder = () => {
    // TODO: Navigate to reorder/purchase orders
    console.log('Navigate to reorder');
  };

  // CHỈ dùng cờ `isLoading` (lần tải ĐẦU TIÊN, chưa có dữ liệu nào), tuyệt đối
  // không gộp `isFetching`.
  //
  // `isFetching` bật lên mỗi lần refetch nền — mà tồn kho đang poll 30 giây/lần
  // và các query khác thì refetch khi quay lại tab. Gộp vào đây nghĩa là cứ 30
  // giây toàn bộ dashboard lại chớp sang skeleton rồi quay về, đúng hiện tượng
  // "thỉnh thoảng trang nháy một phát". Dữ liệu cũ vẫn còn trong cache trong lúc
  // refetch, nên cứ hiển thị tiếp — người dùng không cần thấy gì cả.
  //
  // Bug #32: hai query chạy theo dropdown của panel (top-products-by-category và
  // customer-summary theo kỳ) KHÔNG được gộp vào đây. Đổi queryKey là react-query
  // coi như query mới -> isLoading bật lên -> đổi dropdown của MỘT panel mà cả
  // trang nháy skeleton. Chúng được truyền riêng cho đúng panel ở phần render.
  const isLoading =
    isLoadingDashboardOverview ||
    isLoadingRevenue ||
    isLoadingOrderStatus ||
    isLoadingSuppliers ||
    isLoadingCustomerSummary ||
    isLoadingTransactions ||
    isLoadingLowStock ||
    isLoadingInventory ||
    isLoadingTopProducts;

  return (
    <>
    <PageContainer>
      {/* Page Header */}
      <PageHeader
        title={t('systemOverview')}
        subtitle={t('trackYourBusinessPerformance')}
        actions={
          <div className="grid grid-cols-2 items-center gap-2 sm:flex sm:flex-wrap">
            {/* Store selector dropdown */}
            <div className="relative w-full sm:w-auto">
              <select
                value={selectedStoreId ?? ''}
                onChange={(e) => setSelectedStoreById(e.target.value || null)}
                className="h-10 w-full appearance-none rounded-lg border border-[#9E9E9E] bg-white pl-4 pr-9 text-sm font-medium text-[#666666] hover:bg-slate-50 focus:outline-none focus:ring-1 focus:ring-[#0288D1] sm:w-auto sm:min-w-[150px]"
                style={{ boxShadow: '0px 2px 4px 0px rgba(165,163,174,0.2)' }}
              >
                <option value="">{t('navSelectStore')}</option>
                {availableStores.map((store) => (
                  <option key={store.id} value={store.id}>{store.name}</option>
                ))}
              </select>
              <svg className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M4 6l4 4 4-4" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            {/* Date range */}
            <div className="relative w-full sm:w-auto" ref={datePickerRef}>
              <button
                className="flex h-10 w-full min-w-0 items-center justify-between gap-2 rounded-lg border border-[#9E9E9E] bg-white px-4 text-sm font-medium text-[#666666] hover:bg-slate-50 sm:w-auto sm:justify-start"
                style={{ boxShadow: '0px 2px 4px 0px rgba(165,163,174,0.2)' }}
                onClick={() => setDatePickerOpen((v) => !v)}
              >
                <svg className="shrink-0" width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="2" y="3" width="12" height="11" rx="1.5" stroke="#666" strokeWidth="1.2"/><path d="M2 7h12M5 1v3M11 1v3" stroke="#666" strokeWidth="1.2" strokeLinecap="round"/></svg>
                <span className="truncate">{displayRange}</span>
                <svg className="shrink-0" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M4 6l4 4 4-4" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              {datePickerOpen && (
                <div className="absolute right-0 top-full z-50 mt-2 rounded-xl border border-slate-200 bg-white p-4 shadow-xl">
                  <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">{t('selectDateRange')}</p>
                  <div className="flex items-center gap-3">
                    <div>
                      <label className="mb-1 block text-xs text-slate-500">{t('from')}</label>
                      <input
                        type="date"
                        value={startDate}
                        max={endDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="h-9 rounded-lg border border-slate-200 px-3 text-sm text-slate-700 focus:border-primary-400 focus:outline-none"
                      />
                    </div>
                    <span className="mt-4 text-slate-400">—</span>
                    <div>
                      <label className="mb-1 block text-xs text-slate-500">{t('to')}</label>
                      <input
                        type="date"
                        value={endDate}
                        min={startDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="h-9 rounded-lg border border-slate-200 px-3 text-sm text-slate-700 focus:border-primary-400 focus:outline-none"
                      />
                    </div>
                  </div>
                  <div className="mt-3 flex justify-end">
                    <button
                      onClick={() => setDatePickerOpen(false)}
                      className="rounded-lg bg-primary-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-primary-700"
                    >
                      {t('apply')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        }
      />

      {/* Alert Banner */}
      {showAlert && (outOfStockCount > 0 || lowStockCount > 0) && (
        <div className="mt-6">
          <AlertBanner
            message={`${outOfStockCount} ${t('productsOutOfStock')} · ${lowStockCount} ${t('itemsLowInventory')}`}
            actionLabel={t('review')}
            onAction={handleAlertReview}
            onDismiss={handleAlertDismiss}
          />
        </div>
      )}

        {/* Stats Row */}
        <div className="mt-6">
          <DashboardStatsRow stats={stats} isLoading={isLoading} />
        </div>

        {/* Charts Row */}
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[2fr_1fr]">
          <RevenueChart
            data={revenueData}
            title={t('revenueOverview')}
            subtitle={displayRange}
            isLoading={isLoading}
          />
          <AdvancedInsightsPanel
            data={insightsData}
            isLoading={isLoading || isLoadingCustomersOverview}
            isRefreshing={isRefreshingCustomersOverview}
            period={customersPeriod}
            onPeriodChange={setCustomersPeriod}
          />
        </div>

        {/* Middle Row */}
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_2fr] items-stretch">
          <TopCategoriesPanel
            categories={topCategoriesComputed.categories}
            totalCategories={topCategoriesComputed.totalCategories}
            totalProducts={topCategoriesComputed.totalProducts}
            isLoading={isLoading || isLoadingTopCategories}
            isRefreshing={isRefreshingTopCategories}
            period={categoriesPeriod}
            onPeriodChange={setCategoriesPeriod}
            className="h-full"
          />

          <OrderStatusGrid
            data={orderStatusCardData}
            isLoading={isLoading}
          />
        </div>

        {/* Tables Row */}
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <BestSellingTable
            products={bestSelling}
            isLoading={isLoading}
            onViewAll={handleViewAllProducts}
          />
          <LowStockWarningTable
            items={lowStockItems}
            isLoading={isLoadingLowStock || isLoadingInventory}
            onReorder={handleReorder}
          />
        </div>

        {/* Recent Transactions */}
        <div className="mt-8">
          <RecentTransactionsTable
            transactions={transactions}
            totalCount={transactions.length}
            currentPage={currentPage}
            pageSize={pageSize}
            isLoading={isLoading}
            onPageChange={setCurrentPage}
          />
        </div>
    </PageContainer>

      {/* Alert Popup Modal */}
      {alertPopupOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
          onClick={() => setAlertPopupOpen(false)}
        >
          <div
            className="flex max-h-[90vh] w-full max-w-[520px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-start justify-between border-b border-slate-100 px-6 py-5">
              <div>
                <h3 className="text-lg font-bold text-slate-900">{t('alertDetails')}</h3>
                <p className="text-sm text-slate-400">{t('issuesRequiringAttention')}</p>
              </div>
              <button
                onClick={() => setAlertPopupOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M3 3L11 11M11 3L3 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">

              {/* Out of Stock */}
              <div className="rounded-xl border border-red-100 bg-red-50 p-4">
                <div className="mb-3 flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-100">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M3 5h12M3 5v9a1 1 0 001 1h10a1 1 0 001-1V5M3 5l2-3h8l2 3" stroke="#EF4444" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M7 9h4" stroke="#EF4444" strokeWidth="1.4" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-800">{outOfStockCount} Products Out of Stock</p>
                    <p className="text-xs text-slate-500">{t('theseItemsNeedImmediateRestockingTo')}</p>
                  </div>
                </div>
                <div className="space-y-1">
                  {outOfStockItems.length === 0 ? (
                    <div className="rounded-lg bg-white px-3 py-3 text-sm text-slate-500">
                      No out-of-stock products in selected range.
                    </div>
                  ) : (
                    outOfStockItems.slice(0, 3).map((item, i) => (
                      <div key={`${toText(item.product_id) || i}`} className="flex items-center justify-between rounded-lg bg-white px-3 py-2">
                        <span className="text-sm text-slate-700">{toText(item.product_name) || 'Unknown Product'}</span>
                        <span className="text-xs font-semibold text-red-500">{t('0Units')}</span>
                      </div>
                    ))
                  )}
                </div>
                <div className="mt-3 flex justify-end">
                  <button
                    onClick={() => { navigate(ADMIN_ROUTES.STOCK_IMPORTS); setAlertPopupOpen(false); }}
                    className="rounded-lg bg-red-500 px-4 py-2 text-xs font-semibold text-white hover:bg-red-600"
                  >
                    {t('reorderNow')}
                  </button>
                </div>
              </div>

              {/* Delayed Orders */}
              <div className="rounded-xl border border-orange-100 bg-orange-50 p-4">
                <div className="mb-3 flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-orange-100">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <circle cx="9" cy="9" r="7.5" stroke="#F97316" strokeWidth="1.4"/>
                      <path d="M9 5v4l2.5 2.5" stroke="#F97316" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-800">{t('delayedOrders')}</p>
                    <p className="text-xs text-slate-500">{t('ordersPendingOver15MinutesFromRecen')}</p>
                  </div>
                </div>
                <div className="space-y-1">
                  {delayedOrders.length === 0 ? (
                    <div className="flex items-center justify-between rounded-lg bg-white px-3 py-2">
                      <span className="text-sm text-slate-700">{t('noDelayedOrders')}</span>
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-600">{t('healthy')}</span>
                    </div>
                  ) : (
                    delayedOrders.map((order, i) => (
                      <div key={`${toText(order.order_code) || i}`} className="flex items-center justify-between rounded-lg bg-white px-3 py-2">
                        <span className="text-sm text-slate-700">{toText(order.order_code) || 'Unknown Order'}</span>
                        <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-600">{t('delayed')}</span>
                      </div>
                    ))
                  )}
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-xs text-slate-400">
                    {delayedOrders.length > 0 ? t('delayedOrdersCount', { count: delayedOrders.length }) : t('noDelayedOrdersCurrently')}
                  </span>
                  <button
                    onClick={() => { navigate(ADMIN_ROUTES.ORDERS); setAlertPopupOpen(false); }}
                    className="rounded-lg bg-orange-400 px-4 py-2 text-xs font-semibold text-white hover:bg-orange-500"
                  >
                    {t('viewAllOrders')}
                  </button>
                </div>
              </div>

              {/* Revenue Down */}
              <div className="rounded-xl border border-yellow-100 bg-yellow-50 p-4">
                <div className="mb-3 flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-yellow-100">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M2 13L7 8l3 3 6-7" stroke="#CA8A04" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-800">{t('revenueComparison')}</p>
                    <p className="text-xs text-slate-500">{t('todayComparedWithYesterday')}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg bg-white p-3">
                    <p className="text-xs text-slate-400">{t('today')}</p>
                    <p className="text-lg font-bold text-slate-900">{money.format(totalRevenue, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</p>
                  </div>
                  <div className="rounded-lg bg-white p-3">
                    <p className="text-xs text-slate-400">{t('yesterday')}</p>
                    <p className="text-lg font-bold text-slate-900">{money.format(yesterdayRevenue, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</p>
                  </div>
                </div>
                <div className="mt-3 flex justify-end">
                  <button
                    onClick={() => { navigate(ADMIN_ROUTES.REPORTS); setAlertPopupOpen(false); }}
                    className="rounded-lg bg-yellow-500 px-4 py-2 text-xs font-semibold text-white hover:bg-yellow-600"
                  >
                    {t('viewAnalysis')}
                  </button>
                </div>
              </div>

            </div>

            {/* Footer */}
            <div className="flex justify-center border-t border-slate-100 px-6 py-4">
              <button
                onClick={() => setAlertPopupOpen(false)}
                className="rounded-xl bg-primary-600 px-10 py-2.5 text-sm font-semibold text-white hover:bg-primary-700"
              >
                {t('gotIt')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
